/**
 * CalendarView - Phase 6 (Enhanced)
 * 
 * Beautiful monthly calendar view for tasks featuring:
 * - Gradient header with smooth animations
 * - Visual task indicators with priority colors
 * - Urgency-based styling (overdue, due soon, comfortable)
 * - Elegant hover effects and transitions
 * - Quick task preview on hover/tap
 * - Smooth month navigation with slide animations
 * 
 * @module CalendarView
 */

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Circle,
  CheckCircle2,
  AlertCircle,
  Plus,
  Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Get all days in a month including padding days from prev/next months
 */
function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startDayOfWeek = firstDay.getDay(); // 0 = Sunday
  
  const days = [];
  
  // Previous month padding
  const prevMonth = new Date(year, month, 0);
  const prevMonthDays = prevMonth.getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    days.push({
      date: new Date(year, month - 1, prevMonthDays - i),
      isCurrentMonth: false,
      isPadding: true,
    });
  }
  
  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    days.push({
      date: new Date(year, month, day),
      isCurrentMonth: true,
      isPadding: false,
    });
  }
  
  // Next month padding to fill 6 rows (42 days total)
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({
      date: new Date(year, month + 1, i),
      isCurrentMonth: false,
      isPadding: true,
    });
  }
  
  return days;
}

/**
 * Check if two dates are the same day
 */
function isSameDay(date1, date2) {
  if (!date1 || !date2) return false;
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Check if a date is today
 */
function isToday(date) {
  return isSameDay(date, new Date());
}

/**
 * Format month name
 */
function formatMonth(year, month) {
  return new Date(year, month).toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });
}

// ============================================================================
// PRIORITY & URGENCY COLORS
// ============================================================================

// Priority colors for dots
const PRIORITY_COLORS = {
  high: '#ef4444',
  medium: '#f97316',
  low: '#3b82f6',
};

// Urgency-based colors for day cells
const getUrgencyColor = (task) => {
  if (task.status === 'done') return '#10b981'; // Green for completed
  
  const now = new Date();
  const dueDate = task.dueDate?.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
  const hoursUntilDue = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  if (hoursUntilDue < 0) return '#ef4444'; // Overdue - Red
  if (hoursUntilDue <= 24) return '#f59e0b'; // Due within 24h - Amber
  if (hoursUntilDue <= 72) return '#eab308'; // Due within 3 days - Yellow
  return '#10b981'; // Comfortable - Green
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const TaskDots = ({ tasks, maxDots = 5 }) => {
  const displayTasks = tasks.slice(0, maxDots);
  const remaining = tasks.length - maxDots;

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {displayTasks.map((task, idx) => (
        <motion.div
          key={task.id || idx}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: idx * 0.05 }}
          className={cn(
            "w-2 h-2 rounded-full shadow-sm",
            task.status === 'done' && "opacity-60"
          )}
          style={{
            background: task.status === 'done' 
              ? '#10b981' 
              : PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium,
          }}
          title={task.title}
        />
      ))}
      {remaining > 0 && (
        <span className="text-[10px] font-medium text-gray-400 ml-0.5">
          +{remaining}
        </span>
      )}
    </div>
  );
};

const DayCell = ({ 
  day, 
  tasks, 
  isSelected, 
  onSelect,
  onAddTask,
  isWeekend,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const dayTasks = tasks.filter(task => {
    if (!task.dueDate) return false;
    const dueDate = task.dueDate?.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
    return isSameDay(dueDate, day.date);
  });

  const hasOverdue = dayTasks.some(t => {
    const dueDate = t.dueDate?.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
    return t.status !== 'done' && dueDate < new Date() && !isToday(dueDate);
  });

  const hasDueSoon = dayTasks.some(t => {
    if (t.status === 'done') return false;
    const dueDate = t.dueDate?.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
    const now = new Date();
    const hoursUntilDue = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilDue >= 0 && hoursUntilDue <= 24;
  });

  const todayDate = isToday(day.date);

  return (
    <motion.div
      className={cn(
        "relative min-h-[100px] p-2 cursor-pointer transition-all duration-200",
        "border-b border-r border-gray-100",
        // Base styling
        day.isPadding 
          ? "bg-gray-50/50" 
          : "bg-white",
        // Weekend styling
        isWeekend && !day.isPadding && "bg-gradient-to-br from-slate-50/50 to-gray-50/30",
        // Selected day
        isSelected && "ring-2 ring-teal-500 ring-inset bg-teal-50/30",
        // Hover effect
        !day.isPadding && "hover:bg-gradient-to-br hover:from-teal-50/40 hover:to-cyan-50/30",
        // Overdue styling
        hasOverdue && !day.isPadding && "bg-gradient-to-br from-red-50/40 to-rose-50/30",
        // Due soon styling  
        hasDueSoon && !hasOverdue && !day.isPadding && "bg-gradient-to-br from-amber-50/40 to-orange-50/30",
      )}
      whileHover={{ scale: day.isPadding ? 1 : 1.02 }}
      whileTap={{ scale: 0.98 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={() => onSelect(day.date, dayTasks)}
    >
      {/* Day Number */}
      <div className="flex items-start justify-between">
        {todayDate ? (
          <motion.div 
            className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-500/30"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <span className="text-sm font-bold text-white">
              {day.date.getDate()}
            </span>
          </motion.div>
        ) : (
          <span className={cn(
            "text-sm font-medium transition-colors",
            day.isPadding 
              ? "text-gray-300" 
              : isWeekend 
                ? "text-gray-500"
                : "text-gray-700",
            isSelected && "text-teal-700 font-semibold"
          )}>
            {day.date.getDate()}
          </span>
        )}

        {/* Overdue indicator */}
        {hasOverdue && (
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            className="flex items-center justify-center"
          >
            <AlertCircle size={14} className="text-red-500 animate-pulse" />
          </motion.div>
        )}

        {/* Add task button on hover */}
        <AnimatePresence>
          {isHovered && !day.isPadding && (
            <motion.button
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              className="w-5 h-5 rounded-full bg-teal-500 hover:bg-teal-600 flex items-center justify-center shadow-md"
              onClick={(e) => {
                e.stopPropagation();
                onAddTask?.(day.date);
              }}
            >
              <Plus size={12} className="text-white" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Task Indicators */}
      {dayTasks.length > 0 && (
        <div className="mt-1 space-y-1">
          <TaskDots tasks={dayTasks} />
          
          {/* Task preview - show first task title */}
          {dayTasks.length <= 3 && dayTasks.slice(0, 1).map(task => (
            <motion.div 
              key={task.id}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className={cn(
                "text-[11px] font-medium px-1.5 py-0.5 rounded-md truncate",
                "transition-all duration-200",
                task.status === 'done' 
                  ? "bg-emerald-100 text-emerald-700 line-through" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
              style={{
                borderLeft: `3px solid ${getUrgencyColor(task)}`,
              }}
            >
              {task.title}
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CalendarView = ({
  tasks = [],
  onDateSelect,
  onAddTask,
  selectedDate = null,
}) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selected, setSelected] = useState(selectedDate);

  const calendarDays = useMemo(
    () => getCalendarDays(currentYear, currentMonth),
    [currentYear, currentMonth]
  );

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(y => y - 1);
    } else {
      setCurrentMonth(m => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(y => y + 1);
    } else {
      setCurrentMonth(m => m + 1);
    }
  };

  const handleToday = () => {
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    setSelected(today);
    onDateSelect?.(today, tasks.filter(t => {
      if (!t.dueDate) return false;
      const dueDate = t.dueDate?.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
      return isSameDay(dueDate, today);
    }));
  };

  const handleSelectDate = (date, dayTasks) => {
    setSelected(date);
    onDateSelect?.(date, dayTasks);
  };

  // Determine if a day index is weekend (0 = Sunday, 6 = Saturday)
  const isWeekendDay = (idx) => idx % 7 === 0 || idx % 7 === 6;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-2xl border border-gray-200 shadow-xl shadow-gray-200/50 overflow-hidden"
    >
      {/* Beautiful Gradient Header */}
      <div className="relative bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-500 px-6 py-5">
        {/* Decorative elements */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjEuNSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIi8+PC9nPjwvc3ZnPg==')] opacity-50" />
        
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Previous Month Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePrevMonth}
              className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-white/30 transition-all shadow-lg"
            >
              <ChevronLeft size={20} />
            </motion.button>

            {/* Month/Year Display */}
            <motion.div 
              key={`${currentYear}-${currentMonth}`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2"
            >
              <CalendarIcon size={22} className="text-white/80" />
              <h2 className="text-xl font-bold text-white tracking-wide">
                {formatMonth(currentYear, currentMonth)}
              </h2>
            </motion.div>

            {/* Next Month Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNextMonth}
              className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-white/30 transition-all shadow-lg"
            >
              <ChevronRight size={20} />
            </motion.button>
          </div>

          {/* Today Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleToday}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-teal-700 font-semibold text-sm shadow-lg hover:shadow-xl transition-all"
          >
            <Sparkles size={16} className="text-amber-500" />
            Today
          </motion.button>
        </div>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200">
        {weekdays.map((day, idx) => (
          <div 
            key={day} 
            className={cn(
              "py-3 text-center text-xs font-bold uppercase tracking-wider",
              idx === 0 || idx === 6 
                ? "text-teal-600" 
                : "text-gray-500"
            )}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, idx) => (
          <DayCell
            key={idx}
            day={day}
            tasks={tasks}
            isSelected={selected && isSameDay(day.date, selected)}
            onSelect={handleSelectDate}
            onAddTask={onAddTask}
            isWeekend={isWeekendDay(idx)}
          />
        ))}
      </div>

      {/* Legend Footer */}
      <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 border-t border-gray-200">
        <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <span>High Priority</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
            <span>Medium</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
            <span>Low</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <span>Completed</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CalendarView;
